import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Mail, Lock, Save, Eye, EyeOff, CheckCircle, AlertCircle, Building2, Globe, AlertTriangle, Sparkles, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const COUNTRIES = [
  { code: "", label: "— Selecciona tu país —" },
  { code: "ES", label: "España" },
  { code: "AT", label: "Austria" }, { code: "BE", label: "Bélgica" }, { code: "BG", label: "Bulgaria" },
  { code: "HR", label: "Croacia" }, { code: "CY", label: "Chipre" }, { code: "CZ", label: "Chequia" },
  { code: "DK", label: "Dinamarca" }, { code: "EE", label: "Estonia" }, { code: "FI", label: "Finlandia" },
  { code: "FR", label: "Francia" }, { code: "DE", label: "Alemania" }, { code: "GR", label: "Grecia" },
  { code: "HU", label: "Hungría" }, { code: "IE", label: "Irlanda" }, { code: "IT", label: "Italia" },
  { code: "LV", label: "Letonia" }, { code: "LT", label: "Lituania" }, { code: "LU", label: "Luxemburgo" },
  { code: "MT", label: "Malta" }, { code: "NL", label: "Países Bajos" }, { code: "PL", label: "Polonia" },
  { code: "PT", label: "Portugal" }, { code: "RO", label: "Rumanía" }, { code: "SK", label: "Eslovaquia" },
  { code: "SI", label: "Eslovenia" }, { code: "SE", label: "Suecia" },
  { code: "GB", label: "Reino Unido" },
  { code: "US", label: "Estados Unidos" }, { code: "MX", label: "México" }, { code: "AR", label: "Argentina" },
  { code: "CO", label: "Colombia" }, { code: "CL", label: "Chile" }, { code: "PE", label: "Perú" },
  { code: "BR", label: "Brasil" }, { code: "OTHER", label: "Otro país" },
];

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState(user?.name || '');
  const [loading, setLoading] = useState(false);

  // Password
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  // Fiscal
  const [country, setCountry] = useState(user?.country || '');
  const [fiscalName, setFiscalName] = useState(user?.fiscal_name || '');
  const [fiscalNif, setFiscalNif] = useState(user?.fiscal_nif || '');
  const [fiscalAddress, setFiscalAddress] = useState(user?.fiscal_address || '');
  const [fiscalCp, setFiscalCp] = useState(user?.fiscal_cp || '');
  const [fiscalCity, setFiscalCity] = useState(user?.fiscal_city || '');
  const [isProfessional, setIsProfessional] = useState(user?.is_professional || false);
  const [fiscalLoading, setFiscalLoading] = useState(false);

  // Campus subscription
  const [campusSub, setCampusSub] = useState(null); // null = loading, false = no sub, object = active sub
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReasonCategory, setCancelReasonCategory] = useState('');
  const [cancelReasonComment, setCancelReasonComment] = useState('');

  const isSpain = country === 'ES';
  const fiscalMissing = !country || (isSpain && !fiscalCp);
  const redirectAfterFiscal = searchParams.get('redirect');

  // Scroll to fiscal section if redirected from Mis Facturas
  useEffect(() => {
    if (searchParams.get('section') === 'fiscal') {
      setTimeout(() => {
        const el = document.getElementById('fiscal-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [searchParams]);

  // Load campus subscription status
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/campus/status');
        if (res.data?.has_access && res.data?.subscription) {
          setCampusSub(res.data.subscription);
        } else {
          setCampusSub(false);
        }
      } catch {
        setCampusSub(false);
      }
    })();
  }, []);

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await api.post('/campus/subscribe/cancel', {
        reason_category: cancelReasonCategory || undefined,
        reason_comment: cancelReasonComment.trim() || undefined,
      });
      toast.success('Renovación cancelada. Mantienes el acceso hasta el final del período pagado.');
      setCampusSub(false);
      setShowCancelConfirm(false);
      setCancelReasonCategory('');
      setCancelReasonComment('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cancelar la suscripción');
    }
    setCancelling(false);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('El nombre no puede estar vacío'); return; }
    setLoading(true);
    try {
      await updateProfile({ name });
      toast.success('Perfil actualizado');
    } catch { toast.error('Error al actualizar'); }
    setLoading(false);
  };

  const handleSaveFiscal = async () => {
    if (!country) { toast.error('Por favor, selecciona tu país de residencia fiscal'); return; }
    if (isSpain && !fiscalCp) { toast.error('El código postal es obligatorio para España'); return; }
    if (isSpain && !fiscalNif) { toast.error('El NIF/NIE es obligatorio para España'); return; }
    setFiscalLoading(true);
    try {
      await updateProfile({ country, fiscal_name: fiscalName, fiscal_nif: fiscalNif, fiscal_address: fiscalAddress, fiscal_cp: fiscalCp, fiscal_city: fiscalCity, is_professional: isProfessional });
      toast.success('Datos fiscales guardados');
      if (redirectAfterFiscal) {
        setTimeout(() => navigate(`/${redirectAfterFiscal}`), 800);
      }
    } catch { toast.error('Error al guardar datos fiscales'); }
    setFiscalLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });
    if (!currentPassword || !newPassword || !confirmPassword) { setPasswordMessage({ type: 'error', text: 'Completa todos los campos' }); return; }
    if (newPassword.length < 6) { setPasswordMessage({ type: 'error', text: 'Mínimo 6 caracteres' }); return; }
    if (newPassword !== confirmPassword) { setPasswordMessage({ type: 'error', text: 'Las contraseñas no coinciden' }); return; }
    setPasswordLoading(true);
    try {
      await api.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      setPasswordMessage({ type: 'success', text: 'Contraseña actualizada' });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setTimeout(() => { setShowPasswordForm(false); setPasswordMessage({ type: '', text: '' }); }, 2000);
    } catch (error) {
      setPasswordMessage({ type: 'error', text: error.response?.data?.detail || 'Error al cambiar la contraseña' });
    }
    setPasswordLoading(false);
  };

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
        <div className="mb-12">
          <div className="decorative-line mb-6" />
          <h1 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-4" data-testid="profile-title">Mi perfil</h1>
          <p className="text-[hsl(var(--text-secondary))]">Gestiona tu información personal y preferencias.</p>
        </div>

        {/* Profile Card */}
        <div className="card-soft mb-8">
          <div className="flex items-center gap-6 mb-8 pb-8 border-b border-[hsl(var(--border))]">
            <div className="w-20 h-20 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center">
              <User className="w-10 h-10 text-[hsl(var(--accent))]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-xl font-heading text-[hsl(var(--text-primary))]">{user?.name}</h2>
              <p className="text-[hsl(var(--text-secondary))]">{user?.email}</p>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-2 ${user?.role === 'admin' ? 'bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]' : 'bg-[hsl(var(--sage))]/20 text-[hsl(var(--sage))]'}`}>
                {user?.role === 'admin' ? 'Administrador' : 'Estudiante'}
              </span>
            </div>
          </div>
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            <h3 className="text-lg font-heading text-[hsl(var(--text-primary))]">Información personal</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--text-tertiary))]" strokeWidth={1.5} />
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="input-soft pl-12" data-testid="profile-name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--text-tertiary))]" strokeWidth={1.5} />
                  <Input value={user?.email} disabled className="input-soft pl-12 opacity-60 cursor-not-allowed" data-testid="profile-email" />
                </div>
              </div>
            </div>
            <Button type="submit" className="btn-gold flex items-center gap-2" disabled={loading} data-testid="profile-save">
              <Save className="w-4 h-4" strokeWidth={1.5} />
              {loading ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </form>
        </div>

        {/* Fiscal Data */}
        <div className="card-soft mb-8" id="fiscal-section">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
            <h3 className="text-lg font-heading text-[hsl(var(--text-primary))]">Datos fiscales</h3>
          </div>
          <p className="text-sm text-[hsl(var(--text-tertiary))] mb-4">
            Necesarios para generar tus facturas correctamente. Si deseas facturas completas con tus datos, rellena todos los campos.
          </p>

          {fiscalMissing && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4" data-testid="fiscal-warning">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">
                {!country ? 'Selecciona tu país de residencia fiscal para que podamos aplicar el IVA correcto en tus facturas.' : 'El código postal es obligatorio para residentes en España.'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>País de residencia fiscal *</Label>
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--text-tertiary))]" strokeWidth={1.5} />
                <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full h-10 pl-12 pr-4 rounded-xl border border-[hsl(var(--border))] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/20" data-testid="fiscal-country">
                  {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre fiscal / Razón social</Label>
              <Input value={fiscalName} onChange={(e) => setFiscalName(e.target.value)} placeholder="Ej: María García López" data-testid="fiscal-name" />
            </div>
            <div className="space-y-2">
              <Label>NIF / DNI / NIE {isSpain && <span className="text-amber-500">*</span>}</Label>
              <Input value={fiscalNif} onChange={(e) => setFiscalNif(e.target.value)} placeholder={isSpain ? "Obligatorio" : "Opcional"} data-testid="fiscal-nif" />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Dirección fiscal</Label>
              <Input value={fiscalAddress} onChange={(e) => setFiscalAddress(e.target.value)} placeholder="Ej: Calle Mayor, 10, 2o B" data-testid="fiscal-address" />
            </div>
            <div className="space-y-2">
              <Label>Código postal {isSpain && <span className="text-amber-500">*</span>}</Label>
              <Input value={fiscalCp} onChange={(e) => setFiscalCp(e.target.value)} placeholder={isSpain ? "Obligatorio" : "Opcional"} data-testid="fiscal-cp" />
            </div>
            <div className="space-y-2">
              <Label>Ciudad</Label>
              <Input value={fiscalCity} onChange={(e) => setFiscalCity(e.target.value)} placeholder="Ej: Madrid" data-testid="fiscal-city" />
            </div>
          </div>
          <div className="mt-5 p-4 bg-[hsl(var(--sage-light))] rounded-xl">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="is-professional-toggle">
              <input
                type="checkbox"
                checked={isProfessional}
                onChange={(e) => setIsProfessional(e.target.checked)}
                className="mt-1 rounded border-[hsl(var(--border))] text-[hsl(var(--accent))] focus:ring-[hsl(var(--accent))]/30"
              />
              <div>
                <p className="text-sm font-medium text-[hsl(var(--text-primary))]">Soy profesional / empresa</p>
                <p className="text-xs text-[hsl(var(--text-tertiary))] mt-0.5">
                  Marca esta casilla si eres autónomo/a o empresa. Se aplicará retención de IRPF (7%) en tus facturas recibidas.
                </p>
              </div>
            </label>
          </div>
          <div className="mt-6">
            <Button onClick={handleSaveFiscal} className="btn-gold flex items-center gap-2" disabled={fiscalLoading} data-testid="save-fiscal-btn">
              <Save className="w-4 h-4" strokeWidth={1.5} />
              {fiscalLoading ? 'Guardando...' : 'Guardar datos fiscales'}
            </Button>
          </div>
        </div>

        {/* Security */}
        <div className="card-soft mb-8">
          <h3 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-6">Seguridad</h3>
          {!showPasswordForm ? (
            <div className="flex items-center justify-between p-4 bg-[hsl(var(--sage-light))] rounded-xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center"><Lock className="w-5 h-5 text-[hsl(var(--sage))]" strokeWidth={1.5} /></div>
                <div><p className="font-medium text-[hsl(var(--text-primary))]">Contraseña</p><p className="text-sm text-[hsl(var(--text-tertiary))]">Cambia tu contraseña</p></div>
              </div>
              <Button variant="outline" className="btn-outline" onClick={() => setShowPasswordForm(true)} data-testid="change-password-toggle">Cambiar</Button>
            </div>
          ) : (
            <div className="p-6 bg-[hsl(var(--sage-light))] rounded-xl">
              <div className="flex items-center justify-between mb-6">
                <h4 className="font-heading text-[hsl(var(--text-primary))]">Cambiar contraseña</h4>
                <button onClick={() => { setShowPasswordForm(false); setPasswordMessage({ type: '', text: '' }); }} className="text-sm text-[hsl(var(--text-tertiary))]">Cancelar</button>
              </div>
              {passwordMessage.text && (
                <div className={`flex items-center gap-3 p-4 rounded-lg mb-6 ${passwordMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {passwordMessage.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                  <span>{passwordMessage.text}</span>
                </div>
              )}
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label>Contraseña actual</Label>
                  <div className="relative">
                    <Input type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="pr-10 bg-white" data-testid="current-password-input" />
                    <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]">{showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Nueva contraseña</Label>
                  <div className="relative">
                    <Input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="pr-10 bg-white" data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]">{showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Confirmar nueva contraseña</Label>
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="bg-white" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" className="btn-gold w-full" disabled={passwordLoading} data-testid="change-password-btn">
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> {passwordLoading ? 'Guardando...' : 'Guardar'}
                </Button>
              </form>
            </div>
          )}
        </div>


        {/* Campus subscription */}
        {campusSub !== null && campusSub !== false && (
          <div className="card-soft mb-8" id="campus-subscription-section" data-testid="campus-subscription-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#c9a227]/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#c9a227]" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-heading text-2xl text-[hsl(var(--text-primary))]">
                  Mi suscripción al Campus
                </h2>
                <p className="text-sm text-[hsl(var(--text-tertiary))]">
                  Campus de Interioridad
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--text-tertiary))] font-sans font-semibold mb-1">Plan</div>
                <div className="text-base text-[hsl(var(--text-primary))] font-medium">
                  {campusSub.plan === 'annual' ? 'Anual' : 'Mensual'}
                </div>
              </div>
              <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--text-tertiary))] font-sans font-semibold mb-1">Estado</div>
                <div className="text-base text-emerald-700 font-medium flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" strokeWidth={2} /> Activa
                </div>
              </div>
              <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--text-tertiary))] font-sans font-semibold mb-1">Alta</div>
                <div className="text-base text-[hsl(var(--text-primary))] font-medium">
                  {campusSub.created_at
                    ? new Date(campusSub.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
                    : '—'}
                </div>
              </div>
            </div>

            {!showCancelConfirm ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border border-[#e8d9a8] bg-[#fdf9f0]">
                <p className="text-sm text-[hsl(var(--text-secondary))] leading-relaxed">
                  Puedes cancelar la renovación automática en cualquier momento. Mantendrás el acceso completo hasta el final del período ya pagado.
                </p>
                <Button
                  onClick={() => setShowCancelConfirm(true)}
                  variant="outline"
                  className="border-[#b45a30]/40 text-[#b45a30] hover:bg-[#b45a30]/5 rounded-full whitespace-nowrap"
                  data-testid="cancel-subscription-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Cancelar renovación
                </Button>
              </div>
            ) : (
              <div className="p-5 rounded-xl border border-[#b45a30]/30 bg-[#b45a30]/5" data-testid="cancel-confirm-panel">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-5 h-5 text-[#b45a30] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2">¿Cancelar la renovación?</h3>
                    <ul className="text-sm text-[hsl(var(--text-secondary))] space-y-1 list-disc pl-5">
                      <li>No se realizarán más cobros automáticos.</li>
                      <li>Conservarás el acceso completo hasta el final del período ya pagado.</li>
                      <li>No hay reembolso parcial del período en curso salvo obligación legal.</li>
                      <li>Podrás volver a suscribirte cuando quieras.</li>
                    </ul>
                  </div>
                </div>

                {/* Optional churn survey */}
                <div className="mt-5 pt-5 border-t border-[#b45a30]/20">
                  <p className="text-sm text-[hsl(var(--text-primary))] font-sans font-medium mb-1">
                    ¿Quieres contarnos por qué? <span className="text-[hsl(var(--text-tertiary))] font-normal">(opcional)</span>
                  </p>
                  <p className="text-xs text-[hsl(var(--text-tertiary))] mb-3 leading-relaxed">
                    Solo si te apetece. Nos ayuda a cuidar mejor lo que ofrecemos.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3" data-testid="cancel-reason-options">
                    {[
                      { value: 'precio', label: 'El precio ahora mismo' },
                      { value: 'tiempo', label: 'No encuentro el momento' },
                      { value: 'contenido', label: 'No conecta con lo que busco' },
                      { value: 'etapa', label: 'Ya he tomado lo que necesitaba' },
                      { value: 'problema_tecnico', label: 'Un problema técnico' },
                      { value: 'otro', label: 'Otro motivo' },
                    ].map(opt => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all text-xs ${
                          cancelReasonCategory === opt.value
                            ? 'border-[#b45a30] bg-[#b45a30]/10 text-[hsl(var(--text-primary))]'
                            : 'border-[#e8d9a8] bg-white hover:border-[#b45a30]/40 text-[hsl(var(--text-secondary))]'
                        }`}
                        data-testid={`cancel-reason-${opt.value}`}
                      >
                        <input
                          type="radio"
                          name="cancel-reason"
                          value={opt.value}
                          checked={cancelReasonCategory === opt.value}
                          onChange={(e) => setCancelReasonCategory(e.target.value)}
                          className="accent-[#b45a30] cursor-pointer"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  <textarea
                    value={cancelReasonComment}
                    onChange={(e) => setCancelReasonComment(e.target.value)}
                    placeholder="Si quieres, añade algo que nos ayude a mejorar…"
                    maxLength={500}
                    rows={2}
                    className="w-full bg-white border border-[#e8d9a8] rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#b45a30] transition-colors"
                    data-testid="cancel-reason-comment"
                  />
                  <div className="text-[10px] text-[hsl(var(--text-tertiary))] text-right mt-1">
                    {cancelReasonComment.length}/500
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button
                    onClick={handleCancelSubscription}
                    className="bg-[#b45a30] hover:bg-[#9a4d28] text-white rounded-full flex-1"
                    disabled={cancelling}
                    data-testid="cancel-subscription-confirm"
                  >
                    {cancelling ? 'Cancelando...' : 'Sí, cancelar la renovación'}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowCancelConfirm(false);
                      setCancelReasonCategory('');
                      setCancelReasonComment('');
                    }}
                    variant="outline"
                    className="rounded-full flex-1"
                    disabled={cancelling}
                    data-testid="cancel-subscription-abort"
                  >
                    Mantener suscripción
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}


        <div className="mt-8 text-center text-sm text-[hsl(var(--text-tertiary))]">
          <p>Miembro desde {user?.created_at ? new Date(user.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' }) : 'recientemente'}</p>
        </div>
      </div>
    </div>
  );
};

export default Profile;
